import type { CourseTreeNode } from "@/types/generated";
import type { Storyboard } from "@/types/storyboard";
import { ContentType } from "@/types/storyboard";
import { StoryboardTextRenderer } from "@/components/storyboard/StoryboardTextRenderer";
import { StoryboardQuizRenderer } from "@/components/storyboard/StoryboardQuizRenderer";
import { StoryboardImageRenderer } from "@/components/storyboard/StoryboardImageRenderer";
import { StoryboardWorksheetRenderer } from "@/components/storyboard/StoryboardWorksheetRenderer";
import { StoryboardCodeRenderer } from "@/components/storyboard/StoryboardCodeRenderer";
import { StoryboardVideoRenderer } from "@/components/storyboard/StoryboardVideoRenderer";
import { StoryboardAudioRenderer } from "@/components/storyboard/StoryboardAudioRenderer";
import { StoryboardFallback } from "@/components/storyboard/StoryboardFallback";
import { useSyncSelectedContentElement } from "@/hooks/useSyncSelectedContentElement";

interface Props {
  contentElement: CourseTreeNode;
  storyboard: Storyboard;
}

export const StoryboardRenderer = ({ contentElement, storyboard }: Props) => {
  useSyncSelectedContentElement();

  const data = storyboard;

  // Handle IMAGE content type
  if (data.contentType === ContentType.IMAGE) {
    return (
      <StoryboardImageRenderer data={data} contentElement={contentElement} />
    );
  }

  // Handle QUIZ content type
  if (data.contentType === ContentType.QUIZ) {
    return (
      <StoryboardQuizRenderer data={data} contentElement={contentElement} />
    );
  }

  // Handle TEXT content type
  if (data.contentType === ContentType.TEXT) {
    return (
      <StoryboardTextRenderer data={data} contentElement={contentElement} />
    );
  }

  // Handle CODE content type
  if (data.contentType === ContentType.CODE) {
    return (
      <StoryboardCodeRenderer data={data} contentElement={contentElement} />
    );
  }

  // Handle VIDEO content type
  if (data.contentType === ContentType.VIDEO) {
    return (
      <StoryboardVideoRenderer data={data} contentElement={contentElement} />
    );
  }

  // Handle AUDIO content type
  if (data.contentType === ContentType.AUDIO) {
    return (
      <StoryboardAudioRenderer data={data} contentElement={contentElement} />
    );
  }

  // Handle WORKSHEET content type
  if (data.contentType === ContentType.WORKSHEET) {
    return (
      <StoryboardWorksheetRenderer
        data={data}
        contentElement={contentElement}
      />
    );
  }

  return <StoryboardFallback title={contentElement.title} />;
};
